import React, { useEffect, useState } from 'react';
import { XCircle, BookmarkSimple, ShareNetwork } from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Verse } from '../../../features/bible/types';
import { Verse as VerseComponent } from '../../../features/bible/components/verses/Verse';
import { useVerseExplanation } from '../../../features/bible/hooks/useVerseExplanation';
import { useBibleStore } from '../../store/bibleStore';
import { ShareVerseModal } from './ShareVerseModal';

interface InfoSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  verse?: Verse;
  verseText?: string;
  chatId?: string;
  threadId?: string;
}

export function InfoSidebar({ 
  isOpen, 
  onClose, 
  verse, 
  verseText,
  chatId,
  threadId 
}: InfoSidebarProps) {
  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const { explanation: verseExplanation, isLoading } = useVerseExplanation(verse);
  const { bookmarkStatuses, toggleBookmark } = useBibleStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Close sidebar when route changes
    onClose();
  }, [location.pathname]);

  const showVerseContent = verse && verseText;

  const handleBookmark = async () => {
    if (!verse) return;
    const reference = `${verse.book} ${verse.chapter}:${verse.verse}`;
    await toggleBookmark(verse, reference);
  };

  const isBookmarked = verse ? bookmarkStatuses[`${verse.book}-${verse.chapter}-${verse.verse}`] : false;

  return (
    <div
      className={`fixed z-[200] top-0 right-0 w-[320px] h-full bg-[#FAF6F1] transform transition-transform duration-300 pb-[30px] ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between px-4 py-2">
          <h2 className="text-base font-medium text-[#361E06]">Verse Details</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[rgba(54,30,6,0.075)]"
          >
            <XCircle className="w-6 h-6 text-[#361E06]" weight="fill"/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {showVerseContent && verse && (
            <div className="pt-5">
              <div className="">
                <VerseComponent 
                  verse={verse}
                  showReference={true}
                  excerptPadding={false}
                  className="bg-[#E2E0FF] p-4"
                />
                <div className="mt-4 px-4 flex gap-3">
                  <button
                    onClick={handleBookmark}
                    className={`group inline-flex items-center px-4 py-2 rounded-full text-sm font-medium gap-2 ${
                      isBookmarked 
                        ? 'bg-[rgba(54,30,6,0.075)] text-[#361E06] hover:bg-[rgba(54,30,6,0.1)]' 
                        : 'bg-[#361E06] text-white hover:bg-[#4A2B0B]'
                    }`}
                  >
                    <BookmarkSimple className="w-4 h-4" weight={isBookmarked ? "fill" : "bold"} />
                    <span className="relative">
                      <span className={`transition-opacity duration-200 ${isBookmarked ? 'group-hover:opacity-0' : ''}`}>
                        {isBookmarked ? 'Saved' : 'Save'}
                      </span>
                      {isBookmarked && (
                        <span className="absolute inset-0 transition-opacity duration-200 opacity-0 group-hover:opacity-100">
                          Remove
                        </span>
                      )}
                    </span>
                  </button>
                  <button
                    onClick={() => setShareModalOpen(true)}
                    className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium gap-2 bg-[#361E06] text-white hover:bg-[#4A2B0B]"
                  >
                    <ShareNetwork className="w-4 h-4" weight="bold" />
                    Share
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="px-5 pt-10">
            <h3 className="font-medium text-[#361E06] mb-4">More about this</h3>
            {isLoading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-[rgba(54,30,6,0.075)] rounded w-3/4" />
                <div className="h-4 bg-[rgba(54,30,6,0.075)] rounded w-5/6" />
                <div className="h-4 bg-[rgba(54,30,6,0.075)] rounded w-4/5" />
              </div>
            ) : (
              <div className="prose prose-sm max-w-none font-newsreader text-xl pb-4">
                <p className="text-[#361E06] whitespace-pre-wrap">
                  {verseExplanation}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {verse && verseText && (
        <ShareVerseModal
          isOpen={isShareModalOpen}
          onClose={() => setShareModalOpen(false)}
          verse={verse}
          verseText={verseText}
        />
      )}
    </div>
  );
} 